import { formatDateTime } from '@/utils/datetime';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CaretDown, WarningCircle } from '@phosphor-icons/react';
import { useT } from '@/i18n/LanguageContext';
import { useFieldMeanings } from './fieldMeanings';
import LearningInfluence from './LearningInfluence';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dms`;

// 主要項目（図面）— 図番・図面名・図面種類・部品名・製品名・材質・縮尺・旧図番
const DRAWING_PRIMARY = ['drawing_number', 'drawing_name', 'document_subtype', 'part_name',
  'assembly_name', 'product_name', 'material_raw', 'scale', 'old_drawing_number', 'revision',
  'circuit_name', 'system_name', 'process_name', 'diagram_name', 'sheet_number'];
const LABEL_OVERRIDE = { material_raw: '材質', drawing_date_raw: '図面の日付' };
const SUBTYPE_FIELD = { key: 'document_subtype', label_ja: '図面種類', required: false };

// 責任者欄は専用の確認UIで扱う（スキーマのキーが正）
const RESP = [['drawn_by', '製図者'], ['designed_by', '設計者'],
  ['checked_by', '検図者'], ['approved_by', '承認者']];
const RESP_KEYS = RESP.map(([k]) => k);

// HT-4.5 · 社員名簿との照合（提案のみ。自動確定・自動選択はしない）
const useMasterMatches = (extraction) => {
  const [matches, setMatches] = useState({});
  const raw = extraction?.raw || {};
  const norm = extraction?.normalized || {};
  const decisions = extraction?.field_decisions || {};
  const entities = extraction?.field_entity_types || {};
  const items = RESP_KEYS.map((k) => ({
    role: k,
    name: (decisions[k] || {}).chosen_value || norm[k] || raw[k] || '',
    entity_type: entities[k] || 'person',
  }));
  const key = JSON.stringify(items);
  useEffect(() => {
    const wanted = JSON.parse(key).filter((i) => i.name);
    if (!wanted.length) { setMatches({}); return; }
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/doc-ai/learning/people-match`,
      { items: wanted.map((i) => ({ name: i.name, entity_type: i.entity_type })) })
      .then((r) => {
        const out = {};
        (r.data?.rows || []).forEach((row, i) => { out[wanted[i].role] = row; });
        setMatches(out);
      })
      .catch(() => setMatches({}));
  }, [key]);
  return matches;
};

const splitFields = (schema, profileKey) => {
  const fields = (schema || []).filter((f) => f.key !== 'drawing_date_raw' && f.key !== 'title'
    && !RESP_KEYS.includes(f.key));
  if (!fields.length) return [[], []];
  const isDrawing = Boolean(profileKey && profileKey.includes('drawing'));
  const primary = [];
  if (isDrawing) {
    DRAWING_PRIMARY.forEach((k) => {
      if (k === 'document_subtype') { primary.push(SUBTYPE_FIELD); return; }
      const f = fields.find((x) => x.key === k);
      if (f) primary.push(f);
    });
  }
  fields.forEach((f) => {
    if (!isDrawing && primary.length < 5 && !primary.includes(f)) primary.push(f);
    else if (isDrawing && f.required && !primary.includes(f)) primary.push(f);
  });
  const detail = fields.filter((f) => !primary.includes(f));
  return [primary, detail];
};

const NeedsReview = ({ reason, testId }) => (
  <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
    title={reason} data-testid={testId}>
    <WarningCircle size={11} weight="fill" />確認が必要
  </span>
);

/** 意味（説明文）— 広い画面では常時表示、狭い画面は情報コントロールの中に入れる。 */
const MeaningLine = ({ text, id, always = false }) => {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  if (always) {
    return (
      <p className="pt-0.5 text-[11px] text-zinc-400" data-testid={`field-meaning-${id}`}>
        意味: {text}
      </p>
    );
  }
  return (
    <>
      <p className="hidden pt-0.5 text-[11px] text-zinc-400 sm:block"
        data-testid={`field-meaning-${id}`}>{text}</p>
      <div className="sm:hidden">
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          aria-label={`${id} の意味を表示`}
          className="mt-1 inline-flex min-h-[32px] items-center rounded-md border border-zinc-200 px-2 text-[11px] text-zinc-500 transition-colors duration-200 hover:bg-zinc-50"
          data-testid={`field-meaning-toggle-${id}`}>意味</button>
        {open && (
          <p className="pt-1 text-[11px] text-zinc-500" data-testid={`field-meaning-mobile-${id}`}>
            {text}
          </p>
        )}
      </div>
    </>
  );
};

const Field = ({ label, value, review, id, source, meaning }) => (
  <div data-testid={`drawing-field-${id}`}>
    <p className="text-xs font-medium text-zinc-500">
      {label}{review && <NeedsReview reason={review} testId={`drawing-review-${id}`} />}
    </p>
    <p className={`text-sm ${value ? 'text-zinc-900' : 'text-zinc-400'}`}>{value || '読み取れませんでした'}</p>
    {value && <Provenance src={source} />}
    <MeaningLine text={meaning} id={id} />
  </div>
);

/** 図面の日付 — 原文を既定で保持し、西暦の確定は折りたたみの中で行う。 */
const DateControl = ({ documentId, date, onConfirmed }) => {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState('raw');
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  if (!date?.raw) return null;
  const decided = date.requires_review === false;

  const confirm = async () => {
    setBusy(true);
    try {
      const body = choice === 'raw' ? { choice: 'raw' }
        : choice === 'custom' ? { choice: 'custom', value: custom }
          : { choice: 'candidate', value: choice };
      const r = await axios.post(`${API}/documents/${documentId}/extraction/date`, body);
      toast.success('図面の日付を確定しました');
      setOpen(false);
      onConfirmed?.(r.data.date);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '日付を確定できませんでした');
    } finally { setBusy(false); }
  };

  return (
    <div className="overflow-hidden rounded-md border border-zinc-200 bg-white sm:col-span-2"
      data-testid="drawing-date-control">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-zinc-50 p-3">
        <div>
          <p className="text-xs font-medium text-zinc-500">
            図面の日付{!decided && <NeedsReview reason={date.reason} testId="drawing-review-drawing_date" />}
          </p>
          <p className="text-sm text-zinc-900" data-testid="drawing-date-raw">原文: {date.raw}</p>
          <p className="text-xs text-zinc-500" data-testid="drawing-date-western">
            西暦: {date.normalized || '未確定'}
          </p>
        </div>
        <button type="button" onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs text-zinc-700 transition-colors duration-200 hover:bg-zinc-50"
          data-testid="drawing-date-toggle">日付を確認する</button>
      </div>
      {open && (
        <div className="space-y-3 border-t border-zinc-200 p-4" data-testid="drawing-date-expanded">
          <div className="flex flex-col space-y-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700">
              <input type="radio" checked={choice === 'raw'} onChange={() => setChoice('raw')}
                className="accent-teal-700" data-testid="drawing-date-opt-raw" />原文のまま保存
            </label>
            {(date.candidates || []).filter((c) => c.value).map((c) => (
              <label key={c.value} className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700">
                <input type="radio" checked={choice === c.value} onChange={() => setChoice(c.value)}
                  className="accent-teal-700" data-testid={`drawing-date-opt-${c.value}`} />{c.label}
              </label>
            ))}
            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700">
              <input type="radio" checked={choice === 'custom'} onChange={() => setChoice('custom')}
                className="accent-teal-700" data-testid="drawing-date-opt-custom" />別の日付を入力
              <input type="date" value={custom} onChange={(e) => { setCustom(e.target.value); setChoice('custom'); }}
                className="rounded-md border border-zinc-200 px-2 py-1 text-xs"
                data-testid="drawing-date-custom-input" />
            </label>
          </div>
          <p className="text-xs text-zinc-400">原文は必ず残ります。確定した西暦は確認した人と日時とともに記録されます。</p>
          <button type="button" onClick={confirm} disabled={busy}
            className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm text-zinc-700 transition-colors duration-200 hover:bg-zinc-50 disabled:opacity-50"
            data-testid="drawing-date-confirm">この日付で確定</button>
        </div>
      )}
    </div>
  );
};

const REGION_JA = {
  title_block: 'タイトル欄', body: '本文', revision_table: '改訂欄',
  revision_block: '改訂欄', table: '表', free_text: '注記・備考',
  drawing_area: '図面領域',
  stamp: '押印', filename: 'ファイル名', unknown: '不明',
};

// 図面の構造（Layer 1）の表示名
const STRUCTURE_JA = {
  title_block: 'タイトル欄', approval: '承認欄（押印）', revision: '改訂欄',
  stamp: '押印', parts_table: '部品表', notes: '注記',
};

/** 取得元: タイトル欄 > MATERIAL */
const Provenance = ({ src }) => {
  if (!src || (!src.region && !src.title_block_field)) return null;
  const parts = [REGION_JA[src.region] || src.region, src.title_block_field]
    .filter(Boolean);
  return (
    <p className="pt-0.5 text-[11px] text-zinc-400" data-testid="field-provenance">
      取得元: {parts.join(' > ')}
    </p>
  );
};

const CONF_JA = (c) => (c === null || c === undefined ? null
  : c >= 80 ? '高' : c >= 50 ? '中' : '低');

const RESP_BADGE = {
  confirmed: ['確認済み', 'bg-emerald-50 border-emerald-200 text-emerald-700'],
  corrected: ['修正済み', 'bg-teal-50 border-teal-200 text-teal-800'],
  candidate: ['候補あり', 'bg-amber-50 border-amber-200 text-amber-800'],
  stale: ['再確認が必要', 'bg-amber-100 border-amber-300 text-amber-900'],
  unreadable: ['判読不能', 'bg-zinc-100 border-zinc-200 text-zinc-500'],
  unknown: ['不明', 'bg-zinc-100 border-zinc-200 text-zinc-500'],
  absent: ['項目なし', 'bg-zinc-50 border-zinc-200 text-zinc-400'],
};

/** 責任者欄 — 項目なし / 判読不能 / 候補あり / 確認済み / 修正済み / 不明 / 再確認が必要。 */
const ResponsibilityField = ({ documentId, fieldKey, label, raw, decided,
                              state, confidence, source, newCandidate, meaning,
                              masterMatch, onSaved }) => {
  const [value, setValue] = useState(decided ?? null);
  const [manual, setManual] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [liveState, setLiveState] = useState(null);
  const candidate = (raw || '').trim();
  const st = liveState
    || (value ? (state === 'stale' ? 'stale' : state === 'corrected' ? 'corrected' : 'confirmed')
      : state === 'unknown' ? 'unknown'
        : state === 'found' || state === 'candidate' ? 'candidate'
          : state || (candidate ? 'candidate' : 'absent'));

  const save = async (choice, v) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/documents/${documentId}/extraction/field`,
        { field: fieldKey, choice, value: v || null });
      setValue(r.data.value);
      setLiveState(r.data.state);
      setEditing(false);
      toast.success(choice === 'unknown' ? `${label}を「不明」として記録しました`
        : `${label}を「${r.data.value}」で確定しました`);
      onSaved?.(fieldKey, r.data.value);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存できませんでした');
    } finally { setBusy(false); }
  };

  const badge = RESP_BADGE[st] || RESP_BADGE.absent;

  return (
    <div data-testid={`resp-field-${fieldKey}`}>
      <p className="flex items-center gap-1.5 text-xs font-medium text-zinc-500">
        {label}
        <span className={`rounded-full border px-1.5 py-0.5 text-[10.5px] ${badge[1]}`}
          data-testid={`resp-state-${fieldKey}`}>{badge[0]}</span>
      </p>
      {value ? (
        <div className="pt-0.5">
          <p className="text-sm text-zinc-800" data-testid={`resp-value-${fieldKey}`}>{value}</p>
          {newCandidate?.value && newCandidate.value !== value && (
            <p className="pt-0.5 text-[11px] text-amber-700" data-testid={`resp-new-candidate-${fieldKey}`}>
              新しい候補: {newCandidate.value}
              {CONF_JA(newCandidate.confidence) && `（信頼度: ${CONF_JA(newCandidate.confidence)}）`}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            {st === 'stale' && (
              <button type="button" disabled={busy} onClick={() => save('confirm')}
                className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                data-testid={`resp-reconfirm-${fieldKey}`}>再確認</button>
            )}
            <button type="button" disabled={busy} onClick={() => setEditing((v) => !v)}
              className="rounded-md border border-zinc-200 px-2 py-0.5 text-[11px] text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
              data-testid={`resp-edit-${fieldKey}`}>修正</button>
          </div>
        </div>
      ) : st === 'candidate' ? (
        <div className="pt-0.5">
          <p className="text-sm text-zinc-800" data-testid={`resp-candidate-${fieldKey}`}>
            候補: {candidate}
            {CONF_JA(confidence) && (
              <span className="ml-2 text-[11px] text-zinc-400">信頼度: {CONF_JA(confidence)}</span>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <button type="button" disabled={busy} onClick={() => save('confirm')}
              className="rounded-md border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] text-teal-800 hover:bg-teal-100 disabled:opacity-50"
              data-testid={`resp-confirm-${fieldKey}`}>確認</button>
            <button type="button" disabled={busy} onClick={() => setEditing((v) => !v)}
              className="rounded-md border border-zinc-200 px-2 py-0.5 text-[11px] text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
              data-testid={`resp-manual-${fieldKey}`}>手入力</button>
            <button type="button" disabled={busy} onClick={() => save('unknown')}
              className="rounded-md border border-zinc-200 px-2 py-0.5 text-[11px] text-zinc-500 hover:bg-zinc-50 disabled:opacity-50"
              data-testid={`resp-unknown-${fieldKey}`}>不明</button>
          </div>
        </div>
      ) : (
        <div className="pt-0.5">
          <p className="text-sm text-zinc-400" data-testid={`resp-empty-${fieldKey}`}>
            {st === 'unreadable' ? '欄はありますが判読できませんでした'
              : st === 'unknown' ? '不明として記録しました'
                : 'この図面にこの欄はありません'}
          </p>
          <button type="button" disabled={busy} onClick={() => setEditing((v) => !v)}
            className="mt-1 rounded-md border border-zinc-200 px-2 py-0.5 text-[11px] text-zinc-600 hover:bg-zinc-50"
            data-testid={`resp-manual-${fieldKey}`}>手入力</button>
        </div>
      )}
      {editing && (
        <div className="flex items-center gap-1.5 pt-1.5">
          <input value={manual} maxLength={40} onChange={(e) => setManual(e.target.value)}
            placeholder="氏名を入力" className="w-36 rounded-md border border-zinc-200 px-2 py-1 text-xs"
            data-testid={`resp-input-${fieldKey}`} />
          <button type="button" disabled={busy || !manual.trim()}
            onClick={() => save('manual', manual.trim())}
            className="rounded-md border border-zinc-200 px-2 py-1 text-[11px] text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            data-testid={`resp-save-${fieldKey}`}>保存</button>
        </div>
      )}
      <Provenance src={source} />
      {masterMatch && masterMatch.match_type !== 'empty'
        && masterMatch.match_type !== 'not_applicable' && (
        <p className="text-[11px] text-zinc-600" data-testid={`resp-master-${fieldKey}`}>
          名簿照合: {masterMatch.match_label_ja}
          {masterMatch.employee && ` — ${masterMatch.employee.label_ja}`}
          {masterMatch.alias_applied && `（承認済みの読み替え: ${masterMatch.alias_applied}）`}
          {masterMatch.candidates?.length > 0
            && ` — 候補: ${masterMatch.candidates.map((c) => c.label_ja).join(' / ')}`}
          <span className="pl-1 text-zinc-400">自動では確定しません</span>
        </p>
      )}
      {source?.entity_type_label_ja && (
        <p className="text-[11px] text-zinc-500" data-testid={`resp-entity-${fieldKey}`}>
          実体の種類: {source.entity_type_label_ja}
          {source.entity_type === 'department' && '（個人ではありません）'}
        </p>
      )}
      {source?.date_candidate && (
        <p className="text-[11px] text-zinc-500" data-testid={`resp-date-candidate-${fieldKey}`}>
          同じ押印の日付候補: {source.date_candidate}
        </p>
      )}
      {source?.conflict && (
        <p className="text-[11px] text-amber-700" data-testid={`resp-conflict-${fieldKey}`}>
          押印が重なっています。候補: {(source.other_candidates || []).join(' / ') || '複数'}
        </p>
      )}
      {source?.reason_ja && (
        <p className="text-[11px] text-zinc-400" data-testid={`resp-reason-${fieldKey}`}>
          理由: {source.reason_ja}
          {source.stamp_kind_label_ja && `（${source.stamp_kind_label_ja}）`}
        </p>
      )}
      <MeaningLine text={meaning} id={fieldKey} always />
    </div>
  );
};

const Collapsible = ({ title, badge, testId, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-md border border-zinc-200" data-testid={testId}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between bg-zinc-50 px-4 py-2.5 text-sm font-medium text-zinc-700 transition-colors duration-200 hover:bg-zinc-100"
        data-testid={`${testId}-toggle`}>
        <span className="flex items-center gap-2">{title}{badge}</span>
        <CaretDown size={13} className={`text-zinc-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="border-t border-zinc-200 bg-white px-4 py-3">{children}</div>}
    </div>
  );
};

/** 図面レビュー — 主要6項目は常時表示、その他は折りたたみ。 */
export const DrawingReviewPanel = ({ documentId, extraction, readInfo, onDateConfirmed }) => {
  const [date, setDate] = useState(extraction?.date || null);
  const { lang } = useT();
  const meanings = useFieldMeanings(extraction?.profile_key, lang === 'en' ? 'en' : 'ja');
  const masterMatches = useMasterMatches(extraction);
  useEffect(() => { setDate(extraction?.date || null); }, [extraction?.date]);
  if (!extraction) return null;
  const n = extraction.normalized || {};
  const raw = extraction.raw || {};
  const reviews = {};
  (extraction.fields_requiring_review || []).forEach((f) => { reviews[f.field] = f.reason; });
  const [primary, detail] = splitFields(extraction.schema_fields, extraction.profile_key);
  const detailReviewCount = detail.filter((f) => reviews[f.key]).length;
  if (!primary.length) return null;
  const val = (k) => (n[k] !== undefined && n[k] !== null ? n[k] : raw[k]);
  const isDrawing = String(extraction.profile_key || '').includes('drawing');
  const states = extraction.field_states || {};
  const sources = extraction.field_sources || {};
  const decisions = extraction.field_decisions || {};
  const schemaKeys = (extraction.schema_fields || []).map((f) => f.key);
  const respFields = RESP.filter(([k]) => schemaKeys.includes(k));
  const diagnostics = Object.keys(extraction.region_texts || {}).length > 0
    || (raw.stamps || []).length > 0 || Boolean(raw.material_raw);
  const mean = (k) => (meanings[k] || {}).meaning;

  return (
    <div className="space-y-3 rounded-md border border-zinc-200 bg-white p-4" data-testid="drawing-review-panel">
      <div className="flex flex-wrap items-baseline gap-2">
        <p className="text-xs font-semibold text-zinc-600">
          読み取った情報（{extraction.profile_label || '書類'}）
        </p>
        <span className="rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs text-zinc-600"
          data-testid="drawing-subtype">{n.document_subtype || extraction.profile_label || '書類'}</span>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {primary.map((f) => (
          <div key={f.key} className={f.key === 'material_raw' ? 'space-y-2' : undefined}>
            <Field id={f.key} label={LABEL_OVERRIDE[f.key] || f.label_ja}
              value={f.key === 'material_raw'
                ? (n.material_display || n.material || raw.material_raw) : val(f.key)}
              source={sources[f.key] || (f.key === 'material_raw' ? sources.material : null)}
              meaning={mean(f.key)}
              review={reviews[f.key] || (f.key === 'material_raw' ? reviews.material : null)} />
            {f.key === 'material_raw' && n.material_family && (
              <Field id="material_family" label="材質分類" value={n.material_family}
                meaning={mean('material_family')} />
            )}
          </div>
        ))}
        <DateControl documentId={documentId} date={date}
          onConfirmed={(d) => { setDate(d); onDateConfirmed?.(d); }} />
      </div>

      {respFields.length > 0 && (
        <div className="rounded-md border border-zinc-200 bg-zinc-50/60 p-3" data-testid="drawing-responsibilities">
          <p className="text-xs font-semibold text-zinc-600">責任者</p>
          <p className="pt-0.5 text-[11px] text-zinc-400">
            読み取れた候補のみを表示します。氏名は自動で作りません。
          </p>
          <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2">
            {respFields.map(([k, label]) => (
              <ResponsibilityField key={k} documentId={documentId} fieldKey={k} label={label}
                raw={raw[k]} decided={(decisions[k] || {}).chosen_value || null}
                state={states[k] || (decisions[k]
                  ? ((decisions[k].chosen_value && 'confirmed') || 'unknown') : null)}
                confidence={(extraction.confidence_by_field || {})[k]}
                source={sources[k]} meaning={mean(k)}
                masterMatch={masterMatches[k]}
                newCandidate={(extraction.field_new_candidates || {})[k]} />
            ))}
          </div>
        </div>
      )}

      <Collapsible title={isDrawing ? '詳細な図面情報' : '詳細な情報'} testId="drawing-detail"
        badge={detailReviewCount > 0 && (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800"
            data-testid="drawing-detail-review-count">確認が必要な項目 {detailReviewCount}件</span>
        )}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {detail.map((f) => (
            <Field key={f.key} id={f.key} label={f.label_ja} value={raw[f.key]}
              source={sources[f.key]} meaning={mean(f.key)} review={reviews[f.key]} />
          ))}
        </div>
      </Collapsible>

      {diagnostics && (
        <Collapsible title="詳細診断" testId="drawing-diagnostics">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <p className="text-[11px] text-zinc-400 sm:col-span-2">
              読み取った文字そのままの記録です。判断は上の項目で行ってください。
            </p>
            {raw.material_raw && <Field id="material-raw" label="材質（原文）" value={raw.material_raw} />}
            {(raw.stamps || []).length > 0 && (
              <div className="sm:col-span-2" data-testid="drawing-field-stamps">
                <p className="text-xs font-medium text-zinc-500">押印・スタンプ（原文）</p>
                <p className="flex flex-wrap gap-1.5 pt-1">
                  {raw.stamps.map((s) => (
                    <span key={s} className="rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs text-zinc-600">{s}</span>
                  ))}
                </p>
                <p className="mt-1 text-xs text-zinc-400">スタンプは書類の状態には反映しません。</p>
              </div>
            )}
            {Object.keys(extraction.region_texts || {}).length > 0 && (
              <div className="sm:col-span-2" data-testid="drawing-field-region-text">
                <p className="text-xs font-medium text-zinc-500">読み取った文字（領域別）</p>
                {Object.entries(extraction.region_texts).map(([k, v]) => (
                  <p key={k} className="mt-1 whitespace-pre-wrap rounded bg-zinc-50 p-2 text-xs text-zinc-500">
                    <span className="text-zinc-400">{k}: </span>{v}
                  </p>
                ))}
              </div>
            )}
            {Object.keys(extraction.region_structure || {}).length > 0 && (
              <div className="sm:col-span-2" data-testid="drawing-diag-structure">
                <p className="text-xs font-medium text-zinc-500">読み取った図面の構造</p>
                <p className="flex flex-wrap gap-1.5 pt-1">
                  {Object.entries(extraction.region_structure).map(([k, v]) => (
                    <span key={k} className="rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10.5px] text-zinc-500">
                      {STRUCTURE_JA[k] || k}: {v.line_count}行 / 確度 {v.confidence}
                    </span>
                  ))}
                </p>
              </div>
            )}
            {Object.keys(extraction.confidence_by_field || {}).length > 0 && (
              <div className="sm:col-span-2" data-testid="drawing-diag-confidence">
                <p className="text-xs font-medium text-zinc-500">信頼度スコア（管理者向け）</p>
                <p className="flex flex-wrap gap-1.5 pt-1">
                  {Object.entries(extraction.confidence_by_field).map(([k, v]) => (
                    <span key={k} className="rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10.5px] text-zinc-500">
                      {k}: {v}
                    </span>
                  ))}
                </p>
              </div>
            )}
            {(extraction.alias_normalizations || []).length > 0 && (
              <div className="sm:col-span-2" data-testid="drawing-diag-alias">
                <p className="text-xs font-medium text-zinc-500">キー正規化の記録</p>
                {extraction.alias_normalizations.map((a) => (
                  <p key={`${a.source_key}-${a.canonical_key}`} className="pt-0.5 text-[10.5px] text-zinc-500">
                    {a.source_key} → {a.canonical_key}
                    {a.dropped ? '（対象外のため保存しません）' : ''}
                  </p>
                ))}
              </div>
            )}
            <Field id="diag-extractor" label="抽出器バージョン"
              value={extraction.extractor_version ? `${extraction.extractor_key || ''} ${extraction.extractor_version}` : null} />
            <Field id="diag-hash" label="内容ハッシュ"
              value={extraction.source_content_hash ? String(extraction.source_content_hash).slice(0, 16) : null} />
          </div>
        </Collapsible>
      )}

      <LearningInfluence documentId={documentId} />

      {readInfo && (
        <Collapsible title="読み取り情報" testId="drawing-read-info">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Field id="read-model" label="読み取りAI" value={readInfo.model} />
            <Field id="read-mode" label="読み取り方法" value={readInfo.processing_mode_label} />
            <Field id="read-doctype" label="書類の種類" value={readInfo.profile_label} />
            <Field id="read-format" label="ファイル形式" value={readInfo.file_format} />
            <Field id="read-profile" label="抽出プロファイル" value={readInfo.extraction_profile} />
            <Field id="read-at" label="読み取り日時"
              value={extraction.read_at ? formatDateTime(extraction.read_at) : null} />
            <Field id="read-fallback" label="フォールバック" value={readInfo.fallback_used ? '使用' : '未使用'} />
            <Field id="read-provider" label="プロバイダー" value={readInfo.provider_label} />
          </div>
        </Collapsible>
      )}
    </div>
  );
};

/** もう一度読み取る — モデル名は出さず、読み取り方法だけを選ばせる。 */
export const RetryModeDialog = ({ open, onClose, onPick, busy }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
      onClick={onClose} data-testid="retry-mode-dialog">
      <div className="w-full max-w-sm rounded-lg border border-zinc-200 bg-white p-5 shadow-lg"
        onClick={(ev) => ev.stopPropagation()}>
        <p className="text-sm font-semibold text-zinc-900">もう一度読み取る</p>
        <p className="mt-1 text-xs text-zinc-500">読み取り方法を選んでください。</p>
        <div className="mt-4 space-y-2">
          <button type="button" disabled={busy} onClick={() => onPick('standard_document')}
            className="w-full rounded-md border border-zinc-200 px-3 py-2.5 text-left text-sm text-zinc-800 transition-colors duration-200 hover:bg-zinc-50 disabled:opacity-50"
            data-testid="retry-mode-standard">
            通常の書類として読み取る
            <span className="mt-0.5 block text-xs text-zinc-400">本文の内容からタイトルと種類を推定します</span>
          </button>
          <button type="button" disabled={busy} onClick={() => onPick('engineering_drawing')}
            className="w-full rounded-md border border-zinc-200 px-3 py-2.5 text-left text-sm text-zinc-800 transition-colors duration-200 hover:bg-zinc-50 disabled:opacity-50"
            data-testid="retry-mode-drawing">
            図面として読み取る
            <span className="mt-0.5 block text-xs text-zinc-400">図面枠（図番・名称・材質・縮尺）を優先して読み取ります</span>
          </button>
        </div>
        <button type="button" onClick={onClose}
          className="mt-4 w-full rounded-md px-3 py-2 text-sm text-zinc-500 transition-colors duration-200 hover:bg-zinc-50"
          data-testid="retry-mode-cancel">キャンセル</button>
      </div>
    </div>
  );
};

export default DrawingReviewPanel;
